import { reactive, readonly } from "vue";
import { User as Auth0User } from "@auth0/auth0-spa-js";
import { captureMessage } from "./sentry";
import { getDomainFromEmail } from "./util/string";
import {
  DiscountConfig,
  CustomerOrderLimit,
  CustomerFeatures,
  CustomerSource,
} from "../../../open-api/cms";
import { useGlobalStore } from "./global-store";
import { watch } from "vue";
import {
  FeatureFlaggedCurrencies,
  SHOP_CUSTOMER_IDS,
  PENDING_CUSTOMER_SELECTION_KEY,
} from "./constants";
import { isAccountFeatureToggle } from "./util/isAccountFeatureToggle";
import jwtDecode from "jwt-decode";
import {
  OpenAPI as CatalogServiceV2,
  OpenAPI as CatalogService,
} from "@workspace/open-api/catalog-service";
import { CompletedOrderItem } from "@workspace/open-api/ros-ui";
import { datadogRum } from "@datadog/browser-rum";
import { OpenAPI as ShopApi } from "@workspace/open-api/shop-middleware";
import { OpenAPI as TransactionsApi } from "@workspace/open-api/transactions-service";

const useNewKYBForm = import.meta.env.VITE_USE_NEW_KYB_FORM === "true";

const FeatureToggleDefaultState = {
  debugViews: false,
  designViews: false,
  staffViews: true,
  customization: true,
  orderHistoryFilters: true,
  enableTransactionCsvDownload: true,
  enableConsentBanner: true,
  enableTopUpInvoices: true,
  userSettings: true,
  enableLowBalanceBadges: true,
  enableCustomerDefaultCurrency: true,
  apiLogs: false,
  enablePeakTradingBanner: false,
  asyncTrade: true,
  shopManagement: true,
  featuredProductsList: true,
  useNewKYBForm: useNewKYBForm,
};

const CurrencyTogglesDefaultState: Record<FeatureFlaggedCurrencies, boolean> = {
  AED: true,
  ARS: true,
  BRL: true,
  CLP: true,
  COP: true,
  EGP: false,
  IDR: false,
  ILS: false,
  INR: true,
  MYR: true,
  PEN: true,
  PHP: false,
  SAR: true,
  THB: false,
  TRY: false,
  VND: false,
};

const CustomerClaimKey = import.meta.env
  .VITE_AUTH0_CUSTOMER_CLAIM_KEY as string;

export type FeatureToggles = typeof FeatureToggleDefaultState;
interface AppState {
  customerId: string | null;
  customerName: string | null;
  customerErr: string | null;
  customerDiscountConfig: DiscountConfig | null;
  customerDefaultCurrency: string;
  orderLimits: [] | Array<CustomerOrderLimit> | null;
  isUserStaff: boolean | null;
  userEmail: string | null;
  userName: string | null;
  userPropertiesPopulated: boolean;
  customerPopulated: boolean;
  isEmailVerified: boolean | null;
  featureToggles: FeatureToggles;
  customerFeatureFlags: {
    allowManualBrandApproval: boolean;
    floats: boolean;
    cardFees: boolean;
    fx: boolean;
    breakage: boolean;
    discounts: boolean;
    enableFraudBlocking: boolean;
  };
  playgroundMode: boolean;
  useBalancesTncAccepted: boolean | null;
  dataProcessingTncAccepted: boolean | null;
  contactEmailAddress: string | null;
  brandApprovalAccessToken: string | null;
  hiddenNewTags: Array<string>;
  isFirstTimeUser: boolean | null;
  customerSource: CustomerSource | null;
  userPermissions: Array<string> | null;
  currencyToggles: Record<FeatureFlaggedCurrencies, boolean>;
  isShopCustomer: boolean | null;
  useRequestMerchantAccessBanner: boolean | null;
  hasOrgId: boolean | null;
  orderItems: Record<string, CompletedOrderItem>;
  shopCountry: string | null;
}

const state = reactive<AppState>({
  customerId: null,
  customerName: null,
  customerErr: null,
  customerDiscountConfig: null,
  isShopCustomer: null,
  orderLimits: null,
  customerDefaultCurrency: "$",
  isUserStaff: false,
  userEmail: null,
  userName: null,
  userPropertiesPopulated: false,
  customerPopulated: false,
  isEmailVerified: null,
  useBalancesTncAccepted: null,
  dataProcessingTncAccepted: null,
  featureToggles: { ...FeatureToggleDefaultState },
  customerFeatureFlags: {
    allowManualBrandApproval: false,
    floats: false,
    cardFees: false,
    fx: true,
    breakage: true,
    discounts: true,
    enableFraudBlocking: false,
  },
  playgroundMode: false,
  contactEmailAddress: null,
  brandApprovalAccessToken: null,
  hiddenNewTags: [],
  isFirstTimeUser: null,
  customerSource: null,
  userPermissions: null,
  currencyToggles: CurrencyTogglesDefaultState,
  useRequestMerchantAccessBanner: true,
  hasOrgId: false,
  orderItems: {},
  shopCountry: null,
});

const store = {
  state: readonly(state),

  populateInitialState() {
    this.loadFromStorage();
  },

  setStateFromAuth0User(user: Auth0User) {
    if (user.email_verified !== undefined) {
      state.isEmailVerified = user.email_verified;
    } else {
      captureMessage("Auth0 email_verified is undefined");
    }
    state.userEmail = user.email || null;
  },

  decodeJwtToken(token: string): any {
    return jwtDecode(token);
  },

  setStatePermissionsFromAuth0JwtToken(decodedToken: any) {
    state.userPermissions = decodedToken.permissions || [];
    useGlobalStore().userPermissions = decodedToken.permissions || [];
  },

  setCustomerIdFromJwtToken(decodedToken: any) {
    state.hasOrgId = !!decodedToken.org_id;
    if (state.hasOrgId) {
      const pendingSelection = sessionStorage.getItem(
        PENDING_CUSTOMER_SELECTION_KEY
      );
      if (pendingSelection) {
        return;
      }

      const customerId = decodedToken[CustomerClaimKey];
      // Only set customer ID if it's not undefined/null
      if (customerId) {
        state.customerId = customerId;
        useGlobalStore().customerId = customerId;
        localStorage.setItem("customerId", customerId);
      }
    }
  },

  setUserPermissions(permissions: Array<string>) {
    state.userPermissions = permissions;
    useGlobalStore().userPermissions = permissions;
  },

  setIsStaff(isStaff: boolean) {
    state.isUserStaff = isStaff;
    datadogRum.setUserProperty("is_staff", isStaff);
  },

  setDataProcessingTncAccepted(termsAccepted: boolean | null) {
    state.dataProcessingTncAccepted = termsAccepted;
  },

  setUseBalancesTnCAccepted(termsAccepted: boolean) {
    state.useBalancesTncAccepted = termsAccepted;
    useGlobalStore().useBalancesTncAccepted = termsAccepted;
  },

  getDataProcessingTncAccepted() {
    return state.dataProcessingTncAccepted;
  },

  getUseBalancesTnCAccepted() {
    return state.useBalancesTncAccepted;
  },

  setUserPropertiesPopulated(populated: boolean) {
    state.userPropertiesPopulated = populated;
  },

  setUsersName(firstName: string, lastName: string) {
    state.userName = `${firstName} ${lastName}`;
    useGlobalStore().userName = state.userName;
  },

  clearIsStaff() {
    state.isUserStaff = false;
  },

  setBrandApprovalAccessToken(token: string) {
    state.brandApprovalAccessToken = token;
    useGlobalStore().brandApprovalAccessToken = token;
  },

  getBrandApprovalAccessToken() {
    return state.brandApprovalAccessToken;
  },

  loadFromStorage() {
    state.customerId = localStorage.getItem("customerId") || null;
    useGlobalStore().customerId = state.customerId;
    state.customerName = localStorage.getItem("customerName") || null;
    state.shopCountry = localStorage.getItem("shopCountry") || null;
    useGlobalStore().shopCountry = state.shopCountry;
    state.customerDefaultCurrency =
      localStorage.getItem("customerDefaultCurrency") || "$";
    state.hiddenNewTags = JSON.parse(
      localStorage.getItem("hiddenNewTags") || "[]"
    );
    state.orderLimits = JSON.parse(
      localStorage.getItem("customerOrderLimits") || "[]"
    );
    state.customerSource = localStorage.getItem(
      "customerSource"
    ) as CustomerSource;
    state.playgroundMode = localStorage.getItem("playgroundMode") === "true";
    state.isShopCustomer = localStorage.getItem("isShopCustomer") === "true";
    useGlobalStore().playgroundMode = state.playgroundMode;

    const customerDiscountConfig = localStorage.getItem(
      "customerDiscountConfig"
    );
    if (customerDiscountConfig) {
      try {
        state.customerDiscountConfig = JSON.parse(customerDiscountConfig);
      } catch (ex) {
        state.customerDiscountConfig = null;
      }
    }
    state.customerFeatureFlags = JSON.parse(
      localStorage.getItem("customerFeatureFlags") || "{}"
    );
    useGlobalStore().customerFeatureFlags = state.customerFeatureFlags;
    // Load feature toggles
    (Object.keys(state.featureToggles) as Array<keyof FeatureToggles>).forEach(
      (key) => {
        const toggleInLocalStorage = localStorage.getItem(
          `featureToggles.${key}`
        );
        // Result will be 'null' if unset or a stringy true/false
        if (toggleInLocalStorage !== null)
          state.featureToggles[key as keyof FeatureToggles] =
            toggleInLocalStorage === "true";

        if (isAccountFeatureToggle(key)) {
          useGlobalStore()[key] = state.featureToggles[key];
        }
      }
    );
    // Assign global value to global store
    useGlobalStore().customerDefaultCurrency = state.customerDefaultCurrency;
    // Load first time user, default to true if not set
    state.isFirstTimeUser = !(
      localStorage.getItem("isFirstTimeUser") === "false"
    );

    const currencyToggles = localStorage.getItem("currencyToggles");
    if (currencyToggles) {
      try {
        state.currencyToggles = JSON.parse(currencyToggles);
        useGlobalStore().currencyToggles = JSON.parse(currencyToggles);
      } catch (e) {
        state.currencyToggles = CurrencyTogglesDefaultState;
        useGlobalStore().currencyToggles = CurrencyTogglesDefaultState;
        localStorage.setItem(
          "currencyToggles",
          JSON.stringify(CurrencyTogglesDefaultState)
        );
      }
    } else {
      useGlobalStore().currencyToggles = CurrencyTogglesDefaultState;
    }
    return state;
  },

  hideNewTag(tagIdentifier: string) {
    state.hiddenNewTags.push(tagIdentifier);
    localStorage.setItem("hiddenNewTags", JSON.stringify(state.hiddenNewTags));
  },
  shouldShowNewTag(app: string) {
    return !state.hiddenNewTags.includes(app);
  },
  isCustomerAllowedManualBrandApproval(): boolean {
    if (
      !state.customerFeatureFlags ||
      state.customerFeatureFlags.allowManualBrandApproval === undefined ||
      state.customerFeatureFlags.allowManualBrandApproval === null
    ) {
      return true;
    }
    return state.customerFeatureFlags.allowManualBrandApproval;
  },
  isFloatsFeatureEnabledForCustomer() {
    return state.customerFeatureFlags.floats;
  },
  isCardFeesFeatureEnabledForCustomer() {
    return state.customerFeatureFlags.cardFees;
  },
  setFeatureToggle(key: keyof FeatureToggles, value: boolean) {
    state.featureToggles[key] = value;
    localStorage.setItem(`featureToggles.${key}`, value.toString());
  },
  getFeatureToggle(feature: keyof FeatureToggles) {
    return store.state.featureToggles[feature];
  },
  setCurrencyToggles(
    currencyToggles: Record<FeatureFlaggedCurrencies, boolean>
  ) {
    state.currencyToggles = currencyToggles;
    useGlobalStore().currencyToggles = currencyToggles;
    localStorage.setItem("currencyToggles", JSON.stringify(currencyToggles));
  },
  resetAllFeatureTogglesToDefault() {
    Object.entries(FeatureToggleDefaultState).forEach(([key, value]) => {
      this.setFeatureToggle(key as keyof FeatureToggles, value);
      console.log(key, value);
    });
  },
  setOrderLimits(limits: Array<CustomerOrderLimit> | null) {
    state.orderLimits = limits;
    localStorage.setItem("customerOrderLimits", JSON.stringify(limits));
  },
  getOrderLimits() {
    return state.orderLimits;
  },
  getCustomerId() {
    return state.customerId;
  },
  setCustomer(
    id: string,
    name: string,
    contactEmailAddress: string,
    customerFeatures: CustomerFeatures,
    customerSource?: CustomerSource,
    limitedAccess?: boolean,
    shopCountry?: string | null
  ) {
    state.customerId = id;
    state.customerName = name;
    state.contactEmailAddress = contactEmailAddress;
    state.customerSource = customerSource || null;
    state.playgroundMode = limitedAccess || false;
    state.isShopCustomer = SHOP_CUSTOMER_IDS.includes(id);
    useGlobalStore().customerSource = state.customerSource;
    useGlobalStore().customerName = name;
    useGlobalStore().customerId = id;
    useGlobalStore().customerContactEmail = contactEmailAddress;
    useGlobalStore().playgroundMode = state.playgroundMode;

    datadogRum.setUserProperty("customer_id", id);

    state.shopCountry = shopCountry ?? null;
    useGlobalStore().shopCountry = state.shopCountry;
    if (state.shopCountry) {
      localStorage.setItem("shopCountry", state.shopCountry);
    } else {
      localStorage.removeItem("shopCountry");
    }

    localStorage.setItem("customerId", state.customerId);
    localStorage.setItem("customerName", state.customerName);
    localStorage.setItem(
      "isShopCustomer",
      state.isShopCustomer ? "true" : "false"
    );
    localStorage.setItem(
      "playgroundMode",
      state.playgroundMode ? "true" : "false"
    );
    this.populateCustomerFeatureFlags(customerFeatures);
    CatalogService.HEADERS = {
      "x-customer-id": id,
    };
    ShopApi.HEADERS = {
      "x-customer-id": id,
    };
    TransactionsApi.HEADERS = {
      "x-customer-id": id,
    };
    state.customerPopulated = true;
  },

  setCustomerDiscountConfig(config: DiscountConfig | null) {
    state.customerDiscountConfig = config;
    localStorage.setItem(
      "customerDiscountConfig",
      JSON.stringify(state.customerDiscountConfig)
    );
  },

  setCustomerDefaultCurrency(currency: string) {
    state.customerDefaultCurrency = currency;
    useGlobalStore().customerDefaultCurrency = currency;
    localStorage.setItem(
      "customerDefaultCurrency",
      state.customerDefaultCurrency
    );

    datadogRum.setUserProperty("customer_default_currency", currency);
  },

  populateCustomerFeatureFlags(customerFeatures: CustomerFeatures) {
    state.customerFeatureFlags.allowManualBrandApproval =
      customerFeatures.allowManualBrandApproval;
    // balances are not available for all customers, hence default state is false
    state.customerFeatureFlags.floats = customerFeatures.balances || false;
    // card fees are paid only by customers who have access to balances
    state.customerFeatureFlags.cardFees = customerFeatures.balances || false;
    // FX access is available for all customers but can be set as false from the CMS
    state.customerFeatureFlags.fx = customerFeatures.fx && true;
    // Set breakage
    state.customerFeatureFlags.breakage = customerFeatures.breakage;
    // Set discounts
    state.customerFeatureFlags.discounts = customerFeatures.discounts;
    // Set enable fraud blocking
    state.customerFeatureFlags.enableFraudBlocking =
      (customerFeatures as any).enableFraudBlocking || false;

    useGlobalStore().customerFeatureFlags = {
      allowManualBrandApproval:
        state.customerFeatureFlags.allowManualBrandApproval,
      floats: state.customerFeatureFlags.floats,
      cardFees: state.customerFeatureFlags.cardFees,
      fx: state.customerFeatureFlags.fx,
      breakage: state.customerFeatureFlags.breakage,
      discounts: state.customerFeatureFlags.discounts,
      enableFraudBlocking: state.customerFeatureFlags.enableFraudBlocking,
    };

    localStorage.setItem(
      "customerFeatureFlags",
      JSON.stringify(state.customerFeatureFlags)
    );
  },

  unmarkFirstTimeUser() {
    state.isFirstTimeUser = false;
    localStorage.setItem("isFirstTimeUser", "false");
  },

  waitForUserPermissionsToPopulate() {
    return new Promise((resolve) => {
      if (state.userPermissions) resolve(true);
      watch(
        () => state.userPermissions,
        (populated) => {
          if (populated) resolve(true);
        }
      );
    });
  },

  waitForUserPropertiesToPopulate() {
    return new Promise((resolve) => {
      if (state.userPropertiesPopulated) resolve(true);
      watch(
        () => state.userPropertiesPopulated,
        (populated) => {
          if (populated) resolve(true);
        }
      );
    });
  },

  getCustomerDiscount() {
    if (state.customerDiscountConfig == null) {
      return 0;
    } else {
      return 1 - state.customerDiscountConfig.flatRateMultiplier;
    }
  },
  isCustomerPopulated() {
    return state.customerPopulated;
  },
  getCustomerDiscountConfig() {
    return state.customerDiscountConfig;
  },

  clearCustomer() {
    state.customerId = null;
    state.customerName = null;
    state.customerDiscountConfig = null;
    state.orderLimits = [];
    state.hasOrgId = false;
    state.shopCountry = null;
    useGlobalStore().shopCountry = null;
    useGlobalStore().customerId = null;
    useGlobalStore().customerName = null;
    useGlobalStore().customerFeatureFlags = null;
    localStorage.removeItem("customerId");
    localStorage.removeItem("customerName");
    localStorage.removeItem("customerDiscountConfig");
    localStorage.removeItem("shopCountry");
  },

  getShopCountry() {
    return state.shopCountry;
  },

  clearUserPermissions() {
    state.userPermissions = [];
  },

  setCustomerErr(err: string) {
    state.customerErr = err;
  },

  clearCustomerErr() {
    state.customerErr = null;
  },

  getCurrentUserEmailDomain() {
    if (state.userEmail) {
      return getDomainFromEmail(state.userEmail);
    }
    return "";
  },

  setOrderItem(orderItem: CompletedOrderItem) {
    state.orderItems[orderItem.id] = orderItem;
  },

  getOrderItem(orderItemId: string): CompletedOrderItem | null {
    return state.orderItems[orderItemId] || null;
  },

  clearOrderItem(orderItemId: string) {
    delete state.orderItems[orderItemId];
  },

  clearAllOrderItems() {
    state.orderItems = {};
  },

  /**
   * Sets and persists customer ID and name across local state, global store, and localStorage.
   * Only accepts non-empty strings; null/empty values remove corresponding localStorage keys.
   *
   * @param customerId - Customer identifier (only non-empty strings are accepted)
   * @param customerName - Customer display name (only non-empty strings are accepted)
   * @returns {void}
   */
  setCustomerIdAndName(customerId: unknown, customerName: unknown) {
    const safeCustomerId =
      typeof customerId === "string" && customerId.length > 0
        ? customerId
        : null;
    const safeCustomerName =
      typeof customerName === "string" && customerName.length > 0
        ? customerName
        : null;

    state.customerId = safeCustomerId;
    state.customerName = safeCustomerName;
    useGlobalStore().customerId = safeCustomerId;
    useGlobalStore().customerName = safeCustomerName;

    if (safeCustomerId) localStorage.setItem("customerId", safeCustomerId);
    else localStorage.removeItem("customerId");
    if (safeCustomerName)
      localStorage.setItem("customerName", safeCustomerName);
    else localStorage.removeItem("customerName");
  },
};

export default store;
